/**
 * Cart discount code — apply / remove via cart/update.js, refresh bundled sections,
 * dispatch cart:change so GWP (and drawer) recalculate totals with cart-level discounts.
 */
(function () {
  'use strict';

  const ROUTES = Shopify.routes.root;

  function getBundledSections() {
    const sections = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
    );
    return [...sections];
  }

  function errorMessage(payload, fallback) {
    return payload?.description || payload?.message || fallback;
  }

  class CartDiscountCode extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this._form = this.querySelector('form');
      this._input = this.querySelector('[name="discount"]');
      this._applyBtn = this.querySelector('.cart-discount-code__apply');
      this._error = this.querySelector('[data-discount-error]');

      this._form?.addEventListener('submit', (e) => this.onSubmit(e));
    }

    async onSubmit(event) {
      event.preventDefault();
      const code = (this._input?.value || '').trim();
      if (!code) {
        this.showError(this.dataset.errorEmpty || 'Enter a discount code');
        return;
      }
      await this.updateDiscount(code);
    }

    showError(message) {
      if (!this._error) return;
      if (message) {
        this._error.textContent = message;
        this._error.hidden = false;
      } else {
        this._error.textContent = '';
        this._error.hidden = true;
      }
    }

    setBusy(busy) {
      const btn = this._applyBtn || this.querySelector('button[type="submit"]');
      if (btn) {
        btn.toggleAttribute('aria-busy', busy);
        btn.disabled = busy;
      }
      if (this._input) this._input.disabled = busy;
      this.querySelectorAll('[data-discount-remove]').forEach((el) => {
        el.disabled = busy;
      });
    }

    async updateDiscount(code) {
      this.showError('');
      this.setBusy(true);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

      const sections = getBundledSections();
      const body = { discount: code };
      if (sections.length) body.sections = sections.join(',');

      try {
        const res = await fetch(`${ROUTES}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          this.showError(errorMessage(data, this.dataset.errorInvalid));
          return;
        }

        if (this._input) this._input.value = '';

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: { baseEvent: 'discount:change', cart: data }
          })
        );
      } catch (e) {
        console.warn('[CartDiscount] Update failed', e);
        this.showError(this.dataset.errorInvalid);
      } finally {
        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        this.setBusy(false);
      }
    }
  }

  if (!window.customElements.get('cart-discount-code')) {
    window.customElements.define('cart-discount-code', CartDiscountCode);
  }

  document.addEventListener(
    'click',
    (event) => {
      const btn = event.target.closest('[data-discount-remove]');
      if (!btn || btn.disabled) return;

      const host = btn.closest('cart-discount-code') || document.querySelector('cart-discount-code');
      if (!host?.updateDiscount) return;

      event.preventDefault();
      host.updateDiscount('');
    },
    true
  );
})();
